import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ArrowRight, Check, Minus, Users, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const SHOP_URL = 'https://shop.thewementality.com/_paylink/TEmioo10k48L';
const NAVY = '#3D4F7C';
const TEAL = '#4FB6C9';

export const shopUrl = (bird, placement) => {
  const p = new URLSearchParams({
    utm_source: 'culture_align',
    utm_medium: 'disc_quiz',
    utm_campaign: 'disc_birds_upgrade',
    utm_content: `${placement}_${(bird || 'unknown').toLowerCase()}`,
  });
  return `${SHOP_URL}?${p.toString()}`;
};

const trackUpgrade = (leadId, bird, placement) => {
  if (leadId) axios.post(`${API}/disc-quiz/track-cta-click`, { lead_id: leadId, placement, bird }).catch(() => {});
  axios.post(`${API}/analytics/event`, { type: 'disc_quiz_upgrade_clicked', meta: { bird, placement } }).catch(() => {});
};

const INTROS = {
  Eagle: 'As an Eagle, you move fast toward results. Your full report shows how that drive looks in your natural style versus how you adapt at work, how you may come across under stress, and exactly how others can communicate with you.',
  Parrot: 'As a Parrot, you lead with energy and connection. Your full report shows how your natural and adapted styles differ, what your conflict patterns look like, and how to build rapport with detail-focused Owls and steady Doves.',
  Dove: 'As a Dove, you bring harmony and support. Your full report shows what you need under stress, how you typically respond in conflict, and how to work confidently with direct, fast-moving Eagles.',
  Owl: 'As an Owl, you value accuracy and quality. Your full report shows how your standards show up under pressure, where your strengths may be overused, and how to connect with fast-paced Eagles and Parrots.',
};

const tieIntro = (birds) =>
  `Your answers showed a close blend of ${birds.join(', ').replace(/, ([^,]*)$/, ' and $1')}. A 10-question quiz can't separate a blend that close. The full assessment measures your natural and adapted styles across all four, so you can see which style leads, and when.`;

const ROWS = [
  ['Assessment', '10 quick questions', 'Complete DISC behavioral assessment'],
  ['Your style', 'Main and secondary Bird', 'Your natural and adapted style graphs'],
  ['Report', 'Free Bird communication guide (PDF)', 'Personalized Taking Flight with DISC report'],
  ['Strengths', 'General style strengths', 'Your strengths, what you bring to an organization, and how strengths can be overused'],
  ['Stress & conflict', 'General stress tendencies', 'How you may appear under stress, what you need, and your typical conflict behaviors'],
  ['Communication', 'General tips', "Do's and don'ts for communicating with you, plus how to connect with every Bird"],
  ['Motivators & needs', null, 'What motivates you and what you need to do your best work'],
  ['Action plan', 'One growth step', 'Your personal Flight Plan'],
];

const Cell = ({ value }) => (value
  ? <span>{value}</span>
  : <span className="inline-flex items-center text-slate-400"><Minus className="h-4 w-4" /><span className="sr-only">Not included</span></span>);

const Comparison = () => (
  <div className="mt-8 rounded-2xl bg-white text-slate-800 overflow-hidden" data-testid="fyb-compare">
    <div className="hidden sm:grid grid-cols-[1fr_1.2fr_1.6fr] text-xs font-bold uppercase tracking-wider bg-slate-50 border-b border-slate-200">
      <div className="p-3" />
      <div className="p-3 text-slate-500">Free Bird Quiz</div>
      <div className="p-3" style={{ color: NAVY }}>Full DISC BIRDS Assessment · $69</div>
    </div>
    {ROWS.map(([label, free, paid]) => (
      <div key={label} className="grid sm:grid-cols-[1fr_1.2fr_1.6fr] border-b border-slate-100 last:border-0 text-sm">
        <div className="px-4 pt-3 sm:p-3 font-semibold text-slate-900">{label}</div>
        <div className="px-4 sm:p-3 text-slate-500"><span className="sm:hidden font-medium">Free: </span><Cell value={free} /></div>
        <div className="px-4 pb-3 sm:p-3 flex items-start gap-2 font-medium" style={{ color: NAVY }}>
          <Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: TEAL }} />
          <span><span className="sm:hidden">Full: </span>{paid}</span>
        </div>
      </div>
    ))}
  </div>
);

const BuyButton = ({ result, placement, className = '', children }) => (
  <a
    href={shopUrl(result.primary_style, placement)}
    target="_blank"
    rel="noopener noreferrer"
    onClick={() => trackUpgrade(result.lead_id, result.primary_style, placement)}
    className={`inline-flex items-center justify-center font-bold rounded-2xl transition-transform hover:scale-[1.02] ${className}`}
    data-testid={`fyb-upgrade-${placement}-btn`}
  >
    {children}
  </a>
);

export const UpgradeSection = ({ result, sectionRef }) => {
  const birds = result.tied && result.tied_birds?.length > 1 ? result.tied_birds : null;
  return (
    <section
      ref={sectionRef}
      className="rounded-3xl p-6 sm:p-10 text-white shadow-2xl mb-8"
      style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #2b3a5e 55%, ${TEAL} 140%)` }}
      data-testid="fyb-upgrade-section"
    >
      <p className="text-xs font-bold uppercase tracking-widest opacity-80">Go beyond the mini quiz</p>
      <h2 className="text-2xl sm:text-4xl font-bold mt-2 mb-4 leading-tight">
        See the full picture of how you communicate, decide, and handle pressure
      </h2>
      <p className="text-base sm:text-lg opacity-95 leading-relaxed max-w-3xl" data-testid="fyb-upgrade-intro">
        {birds ? tieIntro(birds) : (INTROS[result.primary_style] || INTROS.Eagle)}
      </p>
      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <BuyButton result={result} placement="results_upgrade" className="bg-white px-8 py-4 text-lg shadow-xl w-full sm:w-auto" >
          <span style={{ color: NAVY }} className="inline-flex items-center">Get the Full DISC BIRDS Assessment · $69 <ArrowRight className="ml-2 h-5 w-5" /></span>
        </BuyButton>
        <p className="text-sm opacity-80 inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Secure checkout on The WE Mentality store</p>
      </div>
      <Comparison />
    </section>
  );
};

export const StickyUpgradeBar = ({ result, sectionRef }) => {
  const [show, setShow] = useState(false);
  const passed = useRef(false);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting && e.boundingClientRect.top < 0) passed.current = true;
      setShow(passed.current && !e.isIntersecting);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [sectionRef]);
  if (!show) return null;
  return (
    <div className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-2.5 flex items-center justify-between gap-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)]" data-testid="fyb-sticky-upgrade">
      <div className="text-xs leading-tight text-slate-700"><strong className="block text-sm" style={{ color: NAVY }}>Full DISC Report</strong>$69 · personalized</div>
      <BuyButton result={result} placement="mobile_sticky" className="bg-[#3D4F7C] text-white px-5 py-2.5 text-sm">
        Get it <ArrowRight className="ml-1 h-4 w-4" />
      </BuyButton>
    </div>
  );
};

export const TeamTrainingLink = ({ result }) => {
  const params = new URLSearchParams({ service: 'Team Building & Culture Alignment', bird: result.primary_style || '', source: 'disc-quiz-team-training' });
  return (
    <div className="mt-10 text-center text-sm text-slate-600" data-testid="fyb-team-training">
      <Users className="inline h-4 w-4 mr-1.5 -mt-0.5" style={{ color: TEAL }} />
      Bringing DISC to your team?{' '}
      <a
        href={`/workshops?${params.toString()}#workshop-inquiry`}
        onClick={() => axios.post(`${API}/analytics/event`, { type: 'disc_quiz_secondary_cta_clicked', meta: { primary: result.primary_style, cta: 'team-training' } }).catch(() => {})}
        className="font-semibold underline-offset-2 hover:underline"
        style={{ color: NAVY }}
        data-testid="fyb-team-training-link"
      >
        Ask about team training →
      </a>
    </div>
  );
};
